import { communityInboxApi } from "@circle-react/api/communityInboxApi";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import type { Chat } from "@circle-react/types/CommunityInbox/Chat";
import { infiniteQueryOptions, queryOptions, useMutation } from "react-query";

export const communityInboxChatKey = createQueryKeyFactory(
  "community-inbox-chat",
);

const AVATAR_CHATS_PER_PAGE = 20;

interface CommunityInboxChatsPage {
  records: Chat[];
}

interface CommunityInboxChatsPaginatedPage extends CommunityInboxChatsPage {
  has_next_page: boolean;
  page: number;
}

export const communityInboxChatQueries = {
  latestAvatarChat: () =>
    queryOptions({
      queryKey: communityInboxChatKey.key("latest-avatar-chat"),
      queryFn: () =>
        reactQueryGet<CommunityInboxChatsPage>(
          internalApi.communityInbox.index({
            params: {
              filter: "mine",
              sort: "latest",
              runtime: "ruby_llm_v1",
              has_messages: true,
              per_page: 1,
            },
          }),
        ),
      select: ({ records }) => records[0],
      // Catches activity missed while the tab was asleep or offline.
      refetchOnWindowFocus: true,
    }),
  avatarChats: () =>
    infiniteQueryOptions({
      initialPageParam: 1,
      queryKey: communityInboxChatKey.key("avatar-chats"),
      queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
        reactQueryGet<CommunityInboxChatsPaginatedPage>(
          internalApi.communityInbox.index({
            params: {
              filter: "mine",
              sort: "latest",
              runtime: "ruby_llm_v1",
              has_messages: true,
              per_page: AVATAR_CHATS_PER_PAGE,
              page: pageParam,
            },
          }),
        ),
      getNextPageParam: (lastPage: CommunityInboxChatsPaginatedPage) =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
    }),
};

export const communityInboxChatInvalidations = {
  avatarChats: () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: communityInboxChatKey.key("latest-avatar-chat"),
      }),
      queryClient.invalidateQueries({
        queryKey: communityInboxChatKey.key("avatar-chats"),
      }),
    ]),
};

export const useMemberMarkAsReadMutation = () =>
  useMutation({
    mutationFn: communityInboxApi.memberMarkAsRead,
    onSuccess: communityInboxChatInvalidations.avatarChats,
  });
